import React, { useState, useEffect } from 'react';
import { 
  X, ChevronLeft, ChevronRight, Users, Maximize, Wifi, Wind, 
  Coffee, Tv, Bath, Mountain, Check, Calendar, ArrowRight,
  Star, MapPin, Shield, MessageCircle, Phone
} from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { amenityLabel } from '../i18n/roomsI18n';
import { optimized, srcSetFor } from '../utils/img';
import { hotelInfo } from '../data/mock';

const RoomDetailModal = ({ 
  isOpen, 
  onClose, 
  room, 
  checkIn, 
  checkOut, 
  guests,
  priceData,
  onBook 
}) => {
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showBookingStep, setShowBookingStep] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentImageIndex(0);
      setShowBookingStep(false);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !room) return null;

  const images = room.images || [room.image];
  const nights = calculateNights(checkIn, checkOut);
  // priceData shape from Rooms.jsx getRoomPrice(): {total, perNight, isLive, currency}
  // Fall back to legacy naming just in case, then to room.price.
  const pricePerNight =
    priceData?.perNight ??
    priceData?.price_per_night ??
    room.price;
  const totalPrice =
    priceData?.total ??
    priceData?.total_price ??
    (pricePerNight * nights);
  const isLivePrice = Boolean(priceData?.isLive);

  function calculateNights(start, end) {
    if (!start || !end) return 1;
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  // Generate price breakdown by day
  function generatePriceBreakdown() {
    const breakdown = [];
    const startDate = new Date(checkIn);
    for (let i = 0; i < nights; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      breakdown.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        price: pricePerNight
      });
    }
    return breakdown;
  }

  const priceBreakdown = generatePriceBreakdown();

  // Room amenities with icons
  const amenityIcons = {
    'Free WiFi': Wifi,
    'WiFi': Wifi,
    'Air Conditioning': Wind,
    'Air conditioning': Wind,
    'Mountain View': Mountain,
    'Sea View': Mountain,
    'Balcony': Mountain,
    'Coffee Machine': Coffee,
    'TV': Tv,
    'Flat-screen TV': Tv,
    'Private Bathroom': Bath,
    'Minibar': Coffee,
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleFinalize = () => {
    setShowBookingStep(true);
  };

  const handleConfirmBooking = () => {
    onBook(room, { checkIn, checkOut, guests, totalPrice });
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[95vh] sm:rounded-2xl sm:max-w-5xl overflow-hidden shadow-2xl flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-4 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {showBookingStep && (
              <button
                onClick={() => setShowBookingStep(false)}
                className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center mr-2"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h2 className="text-lg sm:text-xl font-semibold">
                {showBookingStep ? 'Complete Your Booking' : room.name}
              </h2>
              <p className="text-slate-300 text-sm">
                {formatDate(checkIn)} → {formatDate(checkOut)} · {nights} {nights === 1 ? 'night' : 'nights'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!showBookingStep ? (
          /* Room Detail View */
          <div className="flex-1 overflow-y-auto">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left: Image Gallery */}
              <div className="bg-slate-100">
                {/* Main Image */}
                <div className="relative aspect-[4/3] bg-slate-200">
                  <img
                    src={optimized(images[currentImageIndex], 1200)}
                    srcSet={srcSetFor(images[currentImageIndex], [800, 1200, 1600])}
                    sizes="(min-width: 1024px) 60vw, 100vw"
                    alt={room.name}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Navigation Arrows */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  <div className="absolute bottom-3 right-3 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </div>

                {/* Thumbnail Strip */}
                {images.length > 1 && (
                  <div className="flex gap-2 p-3 overflow-x-auto">
                    {images.map((img, idx) => (
                      <button
                        key={`${img}-${idx}`}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                          idx === currentImageIndex ? 'border-amber-500' : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img src={optimized(img, 400)} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Room Details */}
              <div className="p-5 sm:p-6 flex flex-col">
                {/* Price Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">{room.name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-slate-600">
                      <span className="flex items-center gap-1">
                        <Maximize className="w-4 h-4" />
                        {room.size}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {room.maxGuests} Guests
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-amber-600">€{Math.round(totalPrice)}</p>
                    <p className="text-slate-500 text-sm">€{Math.round(pricePerNight)}/night{isLivePrice ? ' avg' : ''}</p>
                  </div>
                </div>

                {/* Your Stay Breakdown */}
                <div className="bg-slate-50 rounded-xl p-4 mb-4">
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-600" />
                    Your Stay
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {priceBreakdown.map((day) => (
                      <div key={day.date} className="flex justify-between text-sm">
                        <span className="text-slate-600">{day.date}</span>
                        <span className="font-medium text-slate-800">€{Math.round(day.price)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-200 mt-3 pt-3 flex justify-between font-semibold">
                    <span>Total ({nights} nights)</span>
                    <span className="text-amber-600">€{Math.round(totalPrice)}</span>
                  </div>
                </div>

                {/* Room Description */}
                <div className="mb-4">
                  <h4 className="font-semibold text-slate-800 mb-2">About this room</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{room.description}</p>
                </div>

                {/* Room Amenities */}
                <div className="mb-4">
                  <h4 className="font-semibold text-slate-800 mb-3">{t('nav.amenities')}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {room.amenities?.map((amenity) => {
                      const IconComponent = amenityIcons[amenity] || Check;
                      return (
                        <div key={amenity} className="flex items-center gap-2 text-slate-600 text-sm">
                          <IconComponent className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          {amenityLabel(amenity, t)}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Standard Features */}
                <div className="mb-6">
                  <h4 className="font-semibold text-slate-800 mb-2">Standard Features</h4>
                  <div className="flex flex-wrap gap-2">
                    {['Private Bathroom', 'Towels', 'Linen', 'Daily Housekeeping'].map((feature) => (
                      <span key={feature} className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <div className="mt-auto">
                  <Button
                    onClick={handleFinalize}
                    className="w-full bg-amber-700 hover:bg-amber-800 text-white py-4 text-lg rounded-xl flex items-center justify-center gap-2"
                  >
                    Finalize Your Reservation
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Booking Confirmation Step */
          <div className="flex-1 overflow-y-auto">
            <div className="grid lg:grid-cols-3 gap-0 min-h-full">
              {/* Left Sidebar - Hotel Info */}
              <div className="bg-slate-800 text-white p-6 lg:col-span-1">
                <div className="sticky top-0">
                  {/* Hotel Rating */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="bg-amber-500 text-white px-2 py-1 rounded font-bold text-sm">
                      {hotelInfo.rating}/10
                    </div>
                    <span className="text-slate-300 text-sm">Excellent</span>
                  </div>

                  <h3 className="text-xl font-bold mb-2">{hotelInfo.name}</h3>
                  
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(4)].map((_, i) => (
                      <Star key={`hotel-star-${i}`} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>

                  <div className="flex items-start gap-2 text-slate-300 text-sm mb-6">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{hotelInfo.address}</span>
                  </div>

                  {/* Booking Guarantee */}
                  <div className="bg-slate-700/50 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-green-400" />
                      <span className="font-semibold">Booking Guarantee</span>
                    </div>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-green-400" />
                        Best Price Guaranteed
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-green-400" />
                        Instant Confirmation
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-green-400" />
                        Free Cancellation
                      </li>
                    </ul>
                  </div>

                  {/* Need Help */}
                  <div className="bg-slate-700/50 rounded-xl p-4">
                    <h4 className="font-semibold mb-3">Need Help?</h4>
                    <a
                      href={`https://wa.me/${hotelInfo.phone.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat on WhatsApp
                    </a>
                    <a
                      href={`tel:${hotelInfo.phone}`}
                      className="flex items-center gap-2 text-slate-300 hover:text-white mt-3 text-sm"
                    >
                      <Phone className="w-4 h-4" />
                      {hotelInfo.phone}
                    </a>
                  </div>
                </div>
              </div>

              {/* Right - Booking Details */}
              <div className="lg:col-span-2 p-6 bg-white">
                {/* Room Image */}
                <div className="relative aspect-video rounded-xl overflow-hidden mb-6">
                  <img
                    src={optimized(images[0], 800)}
                    alt={room.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-3 left-3 bg-black/70 text-white px-3 py-1 rounded-lg text-sm">
                    {room.name}
                  </div>
                </div>

                {/* Booking Summary */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                  <h4 className="font-bold text-lg text-slate-800 mb-4">Booking Summary</h4>
                  
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-slate-500 text-sm">Check-in</p>
                      <p className="font-semibold text-slate-800">{formatDate(checkIn)}</p>
                      <p className="text-slate-500 text-xs">From 14:00</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm">Check-out</p>
                      <p className="font-semibold text-slate-800">{formatDate(checkOut)}</p>
                      <p className="text-slate-500 text-xs">Until 10:00</p>
                    </div>
                  </div>

                  <div className="border-t border-amber-200 pt-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-600">Room</span>
                      <span className="font-medium">{room.name}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-600">Guests</span>
                      <span className="font-medium">{guests} {guests === 1 ? 'Guest' : 'Guests'}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-600">Duration</span>
                      <span className="font-medium">{nights} {nights === 1 ? 'night' : 'nights'}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-600">Price per night</span>
                      <span className="font-medium">€{Math.round(pricePerNight)}{isLivePrice ? ' avg' : ''}</span>
                    </div>
                    <div className="border-t border-amber-200 mt-3 pt-3 flex justify-between">
                      <span className="font-bold text-lg">Total</span>
                      <span className="font-bold text-2xl text-amber-600">€{Math.round(totalPrice)}</span>
                    </div>
                  </div>
                </div>

                {/* Hotel Description */}
                <div className="mb-6">
                  <h4 className="font-semibold text-slate-800 mb-2">About {hotelInfo.name}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {hotelInfo.description}
                  </p>
                </div>

                {/* Confirm Button */}
                <Button
                  onClick={handleConfirmBooking}
                  className="w-full bg-amber-700 hover:bg-amber-800 text-white py-4 text-lg rounded-xl flex items-center justify-center gap-2"
                >
                  Confirm & Proceed to Payment
                  <ArrowRight className="w-5 h-5" />
                </Button>

                <p className="text-center text-slate-500 text-xs mt-3">
                  You will be redirected to our secure payment page
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomDetailModal;
